import { Injectable, NgZone } from '@angular/core';
import {
  Firestore,
  addDoc,
  collection,
  deleteDoc,
  doc,
  getFirestore,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { BehaviorSubject, Observable, map, of, shareReplay } from 'rxjs';
import { Product, ProductStats } from '../models/product.model';

type ProductDocument = Omit<Product, 'id' | 'createdOn' | 'updatedOn'>;
type ProductPayload = Partial<Product> & Pick<Product, 'name' | 'type' | 'category' | 'shortDescription' | 'status'>;
type FirestoreProduct = Partial<Product> & {
  id: string;
  productName?: string;
  productStatus?: string;
  productType?: string;
  isActive?: boolean;
};

const PRODUCTS_COLLECTION = 'products';
const FALLBACK_IMAGE_URL = 'assets/sr-organic/mushroom-hero.png';

@Injectable({ providedIn: 'root' })
export class ProductService {
  private readonly loadingSubject = new BehaviorSubject<boolean>(true);
  private readonly errorSubject = new BehaviorSubject<string | null>(null);
  private currentProducts: Product[] = [];

  readonly loading$ = this.loadingSubject.asObservable();
  readonly error$ = this.errorSubject.asObservable();
  readonly adminProducts$: Observable<Product[]> = this.getProducts().pipe(
    shareReplay({ bufferSize: 1, refCount: true }),
  );
  readonly products$: Observable<Product[]> = this.getActiveProducts().pipe(
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  constructor(private readonly ngZone: NgZone) {}

  getProducts(): Observable<Product[]> {
    const productsRef = collection(this.getFirestoreOrThrow(), PRODUCTS_COLLECTION);
    const productsQuery = query(productsRef);

    return new Observable<Product[]>((subscriber) => {
      const unsubscribe = onSnapshot(
        productsQuery,
        (snapshot) => {
          this.ngZone.run(() => {
            this.emitProductsSnapshot(snapshot.docs, subscriber);
          });
        },
        (error) => {
          this.ngZone.run(() => {
            console.error('[Firestore Products Read Error]', error);
            this.loadingSubject.next(false);
            this.errorSubject.next(this.getErrorMessage(error, 'Unable to load products.'));
            subscriber.error(error);
          });
        },
      );

      return unsubscribe;
    }).pipe(
      map((products) => {
        this.currentProducts = products;
        return products;
      }),
    );
  }

  getActiveProducts(): Observable<Product[]> {
    const productsRef = collection(this.getFirestoreOrThrow(), PRODUCTS_COLLECTION);
    const productsQuery = query(productsRef, where('status', '==', 'active'));

    return this.listenToProducts(
      productsQuery,
      '[Firestore Active Products Error]',
    );
  }

  getCurrentProducts(): Product[] {
    return this.sortProducts(this.currentProducts);
  }

  getParentProducts(): Product[] {
    return this.getCurrentProducts().filter((product) => product.type === 'parent');
  }

  getParentProducts$(): Observable<Product[]> {
    return this.products$.pipe(map((products) => products.filter((product) => product.type === 'parent')));
  }

  getActiveParentProducts(): Observable<Product[]> {
    const productsRef = collection(this.getFirestoreOrThrow(), PRODUCTS_COLLECTION);
    const productsQuery = query(
      productsRef,
      where('status', '==', 'active'),
      where('type', '==', 'parent'),
    );

    return this.listenToProducts(
      productsQuery,
      '[Firestore Active Parent Products Error]',
    );
  }

  getActiveParentProducts$(): Observable<Product[]> {
    return this.getActiveParentProducts();
  }

  getChildProducts(parentId: string): Product[] {
    return this.getCurrentProducts().filter((product) => product.type === 'child' && product.parentId === parentId);
  }

  getProductById(id: string): Product | undefined {
    return this.currentProducts.find((product) => product.id === id);
  }

  async addProduct(product: ProductPayload): Promise<void> {
    const productsRef = collection(this.getFirestoreOrThrow(), PRODUCTS_COLLECTION);

    const now = new Date().toISOString();
    const normalizedProduct = this.normalizeProduct({
      ...product,
      id: product.id || this.createProductId(product.name),
      createdAt: product.createdAt || product.createdOn || now,
      updatedAt: now,
    });

    await addDoc(productsRef, {
      ...this.toFirestore(normalizedProduct),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  }

  async updateProduct(id: string, product: Partial<Product>): Promise<void> {
    const firestore = this.getFirestoreOrThrow();

    const existingProduct = this.getProductById(id);
    const normalizedProduct = this.normalizeProduct({
      ...existingProduct,
      ...product,
      id,
      createdAt: product.createdAt || product.createdOn || existingProduct?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });

    await updateDoc(doc(firestore, `${PRODUCTS_COLLECTION}/${id}`), {
      ...this.toFirestore(normalizedProduct),
      updatedAt: serverTimestamp(),
    });
  }

  async deleteProduct(id: string): Promise<void> {
    const firestore = this.getFirestoreOrThrow();
    const productDoc = doc(firestore, `${PRODUCTS_COLLECTION}/${id}`);
    await deleteDoc(productDoc);
  }

  getStats(products = this.currentProducts): ProductStats {
    const categories = new Set(products.map((product) => product.category).filter(Boolean));

    return {
      totalProducts: products.length,
      parentProducts: products.filter((product) => product.type === 'parent').length,
      childProducts: products.filter((product) => product.type === 'child').length,
      categories: categories.size,
    };
  }

  createProductId(name = 'product'): string {
    return this.slugify(name) || `product-${this.createId()}`;
  }

  private fromFirestore(product: FirestoreProduct): Product {
    return this.normalizeProduct(product);
  }

  private toFirestore(product: Product): ProductDocument {
    const {
      id: _id,
      createdOn: _createdOn,
      updatedOn: _updatedOn,
      ...documentProduct
    } = product;

    return documentProduct;
  }

  private normalizeProduct(product: FirestoreProduct): Product {
    const now = new Date().toISOString();
    const name = this.toText(product.name) || this.toText(product.productName) || product.id;
    const createdAt = this.toIsoDate(product.createdAt || product.createdOn, now);
    const updatedAt = this.toIsoDate(product.updatedAt || product.updatedOn, createdAt);
    const shortDescription = this.toText(product.shortDescription) || 'S.R.Organic product stream.';
    const longDescription = this.toText(product.longDescription) || shortDescription;
    const category = this.toText(product.category) || 'Products';
    const imageUrl = this.toText(product.imageUrl) || FALLBACK_IMAGE_URL;
    const slug = this.toText(product.slug) || name;
    const productType = this.normalizeProductType(product.type || product.productType);
    const status = this.normalizeStatus(product.status || product.productStatus, product.isActive);

    return {
      id: product.id,
      name,
      slug: this.slugify(slug),
      type: productType,
      parentId: productType === 'child' ? product.parentId || null : null,
      category,
      shortDescription,
      longDescription,
      imageUrl,
      themeColor: this.normalizeThemeColor(product.themeColor),
      status,
      displayOrder: Number(product.displayOrder || 1),
      createdAt,
      updatedAt,
      createdOn: createdAt,
      updatedOn: updatedAt,
    };
  }

  private sortProducts(products: Product[]): Product[] {
    return [...products].sort((a, b) => {
      if (a.type !== b.type) {
        return a.type === 'parent' ? -1 : 1;
      }

      return a.displayOrder - b.displayOrder || a.name.localeCompare(b.name);
    });
  }

  private createId(): string {
    return typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  }

  private slugify(value: unknown): string {
    const text = this.toText(value);

    return value
      ? text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      : '';
  }

  private normalizeThemeColor(themeColor: unknown): string | undefined {
    const color = this.toText(themeColor);
    return color && /^#[0-9a-f]{6}$/i.test(color) ? color : undefined;
  }

  private normalizeProductType(type: unknown): Product['type'] {
    return this.toText(type).toLowerCase() === 'child' ? 'child' : 'parent';
  }

  private normalizeStatus(status: unknown, isActive: unknown): Product['status'] {
    const normalizedStatus = this.toText(status).toLowerCase();

    if (normalizedStatus === 'inactive' || normalizedStatus === 'disabled' || isActive === false) {
      return 'inactive';
    }

    return 'active';
  }

  private listenToProducts(
    productsQuery: ReturnType<typeof query>,
    errorLabel: string,
  ): Observable<Product[]> {
    return new Observable<Product[]>((subscriber) => {
      const unsubscribe = onSnapshot(
        productsQuery,
        (snapshot) => {
          this.ngZone.run(() => {
            this.emitProductsSnapshot(snapshot.docs, subscriber);
          });
        },
        (error) => {
          this.ngZone.run(() => {
            console.error(errorLabel, error);
            this.loadingSubject.next(false);
            this.errorSubject.next(this.getErrorMessage(error, 'Unable to load products.'));
            subscriber.next([]);
          });
        },
      );

      return unsubscribe;
    });
  }

  private getErrorMessage(error: unknown, fallback: string): string {
    if (error instanceof Error && error.message) {
      return error.message;
    }

    return fallback;
  }

  private toIsoDate(value: unknown, fallback: string): string {
    if (typeof value === 'string' && value) {
      return value;
    }

    if (value instanceof Date) {
      return value.toISOString();
    }

    if (value && typeof value === 'object' && 'toDate' in value && typeof value.toDate === 'function') {
      return value.toDate().toISOString();
    }

    return fallback;
  }

  private toText(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
  }

  private missingFirebaseProducts$(): Observable<Product[]> {
    this.loadingSubject.next(false);
    this.errorSubject.next('Firebase is not configured. Add your Firebase config in src/environments/environment.ts.');
    return of([]);
  }

  private getFirestoreOrThrow(): Firestore {
    return getFirestore();
  }

  private emitProductsSnapshot(
    docs: Array<{ id: string; data: () => unknown }>,
    subscriber: { next: (products: Product[]) => void },
  ): void {
    try {
      const rawProducts = docs.map((productDoc) => ({
        id: productDoc.id,
        ...(productDoc.data() as object),
      })) as FirestoreProduct[];
      const products = this.sortProducts(rawProducts.map((product) => this.fromFirestore(product)));

      this.currentProducts = products;
      this.loadingSubject.next(false);
      this.errorSubject.next(null);
      subscriber.next(products);
    } catch (error) {
      console.error('[Firestore Products Mapping Error]', error);
      this.loadingSubject.next(false);
      this.errorSubject.next(this.getErrorMessage(error, 'Unable to map Firestore products.'));
      subscriber.next([]);
    }
  }
}
